import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { gsap, useReveal, prefersReducedMotion } from "@/lib/animations";
import { StatsSection } from "@/components/StatsSection";
import { PromiseBelt } from "@/components/PromiseBelt";
import { ProcessSection } from "@/components/ProcessSection";
import { PhilosophySection } from "@/components/PhilosophySection";
import { TrustMarquee } from "@/components/TrustMarquee";
import { Aurora } from "@/components/Aurora";
import { GoogleRating } from "@/components/GoogleRating";
import { PRODUCTS, TESTIMONIALS, ASSETS } from "@/lib/data";

const HERO_SLIDES = [
  { src: ASSETS.breezeLogoBlue, alt: "Breeze Financial Group", fit: "cover" },
  { src: ASSETS.teamSuits, alt: "The Breeze Financial Group team", fit: "cover" },
  { src: ASSETS.teamWorking, alt: "Breeze team working with clients", fit: "cover" },
];

const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (dir) =>
    setIndex((i) => (i + dir + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    if (paused || prefersReducedMotion()) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, 2800);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div
      data-hero-card
      data-testid="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => {
        setPaused(true);
        setTimeout(() => setPaused(false), 3500);
      }}
      className="relative w-full max-w-xs sm:max-w-sm rounded-3xl overflow-hidden shadow-xl shadow-[#29ABE2]/25 ring-1 ring-white/40 animate-float-slow group"
    >
      <div className="relative aspect-square">
        {HERO_SLIDES.map((s, i) => (
          <img
            key={i}
            src={s.src}
            alt={s.alt}
            data-testid={`hero-slide-${i}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>

      <button
        onClick={() => go(-1)}
        data-testid="hero-carousel-prev"
        aria-label="Previous image"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur text-[#0B132B] flex items-center justify-center shadow-md transition-all duration-200 hover:bg-white hover:scale-110"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        onClick={() => go(1)}
        data-testid="hero-carousel-next"
        aria-label="Next image"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur text-[#0B132B] flex items-center justify-center shadow-md transition-all duration-200 hover:bg-white hover:scale-110"
      >
        <ChevronRight size={18} />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            data-testid={`hero-carousel-dot-${i}`}
            aria-label={`Go to image ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === index ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Hero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        "[data-hero-stagger]",
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", stagger: 0.12 }
      );
      gsap.fromTo(
        "[data-hero-card]",
        { x: 80, opacity: 0, rotate: 4 },
        { x: 0, opacity: 1, rotate: 0, duration: 1.2, ease: "power3.out", delay: 0.3 }
      );
    }, heroRef.current);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      className="relative bg-[#F0EBE0] overflow-hidden grain"
    >
      <Aurora className="z-0" intensity="subtle" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-24 md:pt-40 pb-10 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12 items-center">
        <div className="lg:col-span-7">
          <p
            data-hero-stagger
            className="text-xs tracking-[0.3em] uppercase text-[#0B132B]/70 font-semibold mb-4 md:mb-6"
          >
            Retirement, simplified
          </p>
          <h1
            data-hero-stagger
            className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#0B132B]"
          >
            A simple plan for a{" "}
            <span className="text-gradient-breeze">secure future</span>.
          </h1>
          <p
            data-hero-stagger
            className="mt-5 md:mt-7 text-base md:text-lg text-[#334155] font-light leading-relaxed max-w-xl"
          >
            Money that grows without market risk. Tax-free retirement income. A
            plan you can't outlive — built around your family.
          </p>
          <div data-hero-stagger className="mt-7 md:mt-8 flex flex-wrap gap-3 md:gap-4">
            <Link
              to="/quote"
              data-testid="hero-get-quote-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] text-white font-semibold px-7 md:px-8 py-3.5 md:py-4 transition-all duration-300 hover:bg-[#29ABE2] hover:scale-105"
            >
              Get my free quote <ArrowRight size={17} />
            </Link>
            <Link
              to="/products"
              data-testid="hero-products-button"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-white text-[#0B132B] font-semibold px-8 py-4 border border-[#0B132B]/10 transition-all duration-300 hover:border-[#29ABE2] hover:text-[#29ABE2] hover:scale-105"
            >
              Explore our products
            </Link>
            <Link
              to="/products"
              data-testid="hero-products-link-mobile"
              className="md:hidden inline-flex items-center gap-1.5 text-sm font-semibold text-[#0a6fc2] px-2 py-3.5"
            >
              Explore products <ArrowRight size={14} />
            </Link>
          </div>
          <div data-hero-stagger className="mt-6 md:mt-8">
            <GoogleRating />
          </div>
          <div data-hero-stagger className="hidden md:flex mt-9 md:mt-12 flex-wrap gap-x-10 md:gap-x-12 gap-y-5">
            {[
              ["$150M+", "retirement funds protected annually"],
              ["50+", "A-rated carriers"],
              ["0%", "market downside"],
            ].map(([v, l]) => (
              <div key={l}>
                <div className="font-heading font-black text-3xl text-[#0B132B] tracking-tight">
                  {v}
                </div>
                <p className="text-sm text-[#334155] font-light mt-1.5 max-w-[160px] leading-snug">{l}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <HeroCarousel />
        </div>
      </div>
    </section>
  );
};

const ProductsBento = () => {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section ref={ref} data-testid="products-section" className="bg-[#F9F6F0]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-32">
        <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
          What we do
        </p>
        <h2
          data-reveal
          className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0B132B] max-w-3xl"
        >
          Built around the products that matter most.
        </h2>
        <p data-reveal className="mt-6 text-[#334155] font-light leading-relaxed max-w-xl">
          We focus on a tight set of high-impact products — the ones that protect
          families and build generational wealth — and we partner with 50+
          A-rated carriers behind them.
        </p>

        <div className="mt-10 md:mt-16 grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            const featured = p.featured;
            const isNavy = p.accent === "navy";
            const featBg = isNavy ? "bg-navy-deep-gradient" : "bg-breeze-gradient";
            const featShadow = isNavy ? "shadow-[#0B132B]/40" : "shadow-[#29ABE2]/30";
            return (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-reveal
                data-testid={`product-card-${p.id}`}
                className={`group relative rounded-3xl p-5 md:p-8 flex flex-col justify-between min-h-[200px] md:min-h-[280px] lg:aspect-square transition-all duration-300 hover:-translate-y-1.5 ${
                  featured
                    ? `${featBg} text-white shadow-xl ${featShadow}`
                    : "bg-white border border-[#0B132B]/5 shadow-sm hover:shadow-lg hover:border-[#29ABE2]/30"
                }`}
              >
                <div>
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center mb-4 md:mb-6 ${
                      featured ? "bg-white/20" : "bg-[#29ABE2]/10 text-[#0a6fc2]"
                    }`}
                  >
                    <Icon size={20} className="md:!w-[22px] md:!h-[22px]" />
                  </div>
                  <p
                    className={`text-[10px] md:text-xs tracking-[0.22em] uppercase font-semibold mb-1.5 md:mb-2 ${
                      featured ? "text-white/70" : "text-[#29ABE2]"
                    }`}
                  >
                    {featured ? `Featured · ${p.tag}` : p.tag}
                  </p>
                  <h3 className="font-heading font-black tracking-tight text-lg md:text-2xl leading-tight">
                    {p.name}
                  </h3>
                  <p
                    className={`mt-2 md:mt-3 font-light leading-relaxed text-xs md:text-sm hidden md:block ${
                      featured ? "text-white/85" : "text-[#334155]"
                    }`}
                  >
                    {p.short}
                  </p>
                </div>
                <span
                  className={`mt-4 md:mt-6 inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold ${
                    featured ? "text-white" : "text-[#0a6fc2]"
                  }`}
                >
                  Learn more
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section ref={ref} data-testid="testimonials-section" className="bg-[#EEF2F7] relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-32">
        <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
          Family first
        </p>
        <h2
          data-reveal
          className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none text-[#0B132B] max-w-2xl"
        >
          We don't ask for trust — we earn it.
        </h2>
        <div data-reveal className="mt-6">
          <GoogleRating />
        </div>

        <div className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              data-reveal
              data-reveal-delay={i * 0.1}
              data-testid={`testimonial-card-${i}`}
              className="bg-white rounded-3xl p-8 flex flex-col justify-between shadow-sm border border-[#0B132B]/5"
            >
              <div>
                <Quote size={26} className="text-[#29ABE2] mb-5" fill="#29ABE2" />
                <blockquote className="text-[#334155] italic font-light leading-relaxed">
                  "{t.quote}"
                </blockquote>
              </div>
              <figcaption className="mt-8 pt-6 border-t border-[#0B132B]/8">
                <div className="font-heading font-bold text-[#0B132B]">{t.name}</div>
                <div className="text-sm text-[#334155]/80 mt-1">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaBanner = () => {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section ref={ref} className="bg-[#F9F6F0]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-24">
        <div
          data-reveal
          className="relative rounded-[2.5rem] overflow-hidden bg-[#0B132B] grain"
        >
          <img
            src={ASSETS.familyBeach}
            alt="Family walking on the beach at sunset"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/70 to-transparent" />
          <div className="relative z-10 px-7 sm:px-16 py-14 sm:py-28 max-w-2xl">
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none text-white">
              Your family's future shouldn't depend on the market.
            </h2>
            <p className="mt-5 text-white/70 font-light leading-relaxed">
              Get a free, no-pressure quote in minutes. We'll match you with the
              right strategy from 50+ A-rated carriers.
            </p>
            <Link
              to="/quote"
              data-testid="cta-banner-quote-button"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#29ABE2] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-white hover:text-[#0B132B] hover:scale-105"
            >
              Get my free quote <ArrowUpRight size={17} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <TrustMarquee />
      <PromiseBelt />
      <ProductsBento />
      <ProcessSection />
      <StatsSection />
      <PhilosophySection />
      <Testimonials />
      <CtaBanner />
    </main>
  );
}
